/**
 * B2 — Provisiones de fondos y suplidos: el dinero de los clientes que el despacho custodia
 * y lo que se les ha facturado como suplido en el año. No es ingreso del despacho.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { Note, td, tdR, th, thR } from './common'
import type { ThirdPartyFundsResponse } from '@toga/shared'

export default function FundsTab({ issuerId, year }: { issuerId: string; year: number }) {
  const [data, setData] = useState<ThirdPartyFundsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    const p = new URLSearchParams({ year: String(year) })
    if (issuerId) p.set('issuerId', issuerId)
    api
      .get<ThirdPartyFundsResponse>(`/accounting/third-party-funds?${p}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [issuerId, year])

  if (error) return <div className="p-5 text-xs text-rose-300">{error}</div>
  if (!data) {
    return (
      <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }
  if (data.rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center t-muted text-sm">
        Ningún cliente tiene provisiones de fondos ni suplidos facturados.
      </div>
    )
  }
  const t = data.totals
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-base">
              <th className={th}>Cliente</th>
              <th className={thR}>Provisiones recibidas</th>
              <th className={thR}>Aplicadas a facturas</th>
              <th className={thR}>Devueltas</th>
              <th className={thR}>Saldo del cliente</th>
              <th className={thR}>Suplidos facturados ({year})</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.clientId} className="border-b border-base/50 last:border-0">
                <td className={td}>
                  <Link to={`/clients/${r.clientId}?tab=facturacion`} className="hover:underline">
                    {r.clientName}
                  </Link>
                </td>
                <td className={tdR}>{formatCents(r.receivedCents)}</td>
                <td className={tdR}>{formatCents(r.appliedCents)}</td>
                <td className={tdR}>{formatCents(r.refundedCents)}</td>
                <td className={`${tdR} font-semibold ${r.availableCents ? 'text-amber-300' : ''}`}>
                  {formatCents(r.availableCents)}
                </td>
                <td className={tdR}>{formatCents(r.suppliedInvoicedCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-base font-semibold">
              <td className={td}>Total</td>
              <td className={tdR}>{formatCents(t.receivedCents)}</td>
              <td className={tdR}>{formatCents(t.appliedCents)}</td>
              <td className={tdR}>{formatCents(t.refundedCents)}</td>
              <td className={tdR}>{formatCents(t.availableCents)}</td>
              <td className={tdR}>{formatCents(t.suppliedInvoicedCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Note>
        El saldo del cliente es dinero suyo que el despacho tiene en custodia (cuenta de terceros),
        no ingreso. Los suplidos (tasas, procurador, notaría…) se facturan al cliente pero no son
        base imponible ni ingreso del despacho.
      </Note>
    </div>
  )
}
